import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { useInvestigations } from "@/lib/intel/store";
import { listScanRuns } from "@/lib/intel/feeds.functions";
import { getWorkspace, normalizeWorkspace } from "@/lib/workspace/workspaces";
import { FolderOpen, FileDigit, ShieldAlert, Briefcase } from "lucide-react";

export const Route = createFileRoute("/cases")({
  head: () => ({ meta: [{ title: "Case Index · CARIO INTEL" }] }),
  component: CasesPage,
});

function CasesPage() {
  const { state } = useInvestigations();
  const listScans = useServerFn(listScanRuns);
  const [filter, setFilter] = useState<string>("all");
  const { data: scans = [], isLoading } = useQuery({
    queryKey: ["scan-runs", "cases"],
    queryFn: () => listScans(),
    staleTime: 15_000,
  });

  const useCases = useMemo(() => {
    const seen = new Map<string, number>();
    for (const s of scans) {
      if (!s.workspace) continue;
      const id = normalizeWorkspace(s.workspace);
      seen.set(id, (seen.get(id) ?? 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [scans]);

  const visible = filter === "all"
    ? scans
    : filter === "unlabelled"
      ? scans.filter((s) => !s.workspace)
      : scans.filter((s) => s.workspace && normalizeWorkspace(s.workspace) === filter);

  const completed = visible.filter((s) => s.status === "completed").length;
  // Only average over scans that actually produced a score.
  const scored = visible.filter((s) => typeof s.riskScore === "number");
  const avgRisk = scored.length
    ? Math.round(scored.reduce((a, s) => a + (s.riskScore as number), 0) / scored.length)
    : null;
  const avgTrust = avgRisk === null ? null : Math.max(0, 100 - avgRisk);

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="Case Index"
        title="Investigation case index"
        description="Every scan you run is automatically stored here as a case file — tagged with the dashboard use case it was run from, plus trust score, source count, and direct access to the full dossier, notes, graph, and AI chat."
      >
        <div className="grid gap-3 md:grid-cols-4">
          <StatTile label="Total cases" value={visible.length} />
          <StatTile label="Completed" value={completed} tone="success" />
          <StatTile label="Avg trust" value={avgTrust === null ? "—" : `${avgTrust}`} tone="cyan" />
          <StatTile label="Local notes" value={state.notes.length} tone="amber" />
        </div>

        {(useCases.length > 0 || scans.some((s) => !s.workspace)) && (
          <div className="flex flex-wrap gap-1">
            {([["all", `All · ${scans.length}`]] as Array<[string, string]>)
              .concat(useCases.map(([id, n]) => [id, `${getWorkspace(id as never).label} · ${n}`] as [string, string]))
              .concat(scans.some((s) => !s.workspace) ? [["unlabelled", `Unlabelled · ${scans.filter((s) => !s.workspace).length}`]] as Array<[string, string]> : [])
              .map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFilter(id)}
                  className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${
                    filter === id
                      ? "border-cyan/50 bg-cyan/15 text-cyan"
                      : "border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
          </div>
        )}

        {isLoading && <div className="text-xs text-muted-foreground">Loading case files…</div>}
        {!isLoading && visible.length === 0 && (
          <div className="glass rounded-xl p-6 text-sm text-muted-foreground">
            No case files yet. Run a scan from the <Link to="/dashboard" className="text-cyan underline">command dashboard</Link> — it will appear here automatically.
          </div>
        )}

        <div className="grid gap-2">
          {visible.map((s) => {
            const trust = typeof s.riskScore === "number" ? Math.max(0, 100 - s.riskScore) : null;
            const tone = trust === null ? "text-muted-foreground" : trust >= 70 ? "text-success" : trust >= 40 ? "text-amber" : "text-danger";
            const ws = s.workspace ? getWorkspace(normalizeWorkspace(s.workspace)) : null;
            return (
              <Link
                key={s.id}
                to="/vault/$scanId"
                params={{ scanId: s.id }}
                className="glass rounded-xl p-4 hover:bg-panel/60 transition"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
                    <FolderOpen className="h-5 w-5 text-cyan" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">
                      {s.id.slice(0, 8)} · {s.targetType?.toUpperCase() ?? "TARGET"} · {new Date(s.createdAt).toLocaleString()}
                    </div>
                    <h2 className="mt-1 font-display text-xl truncate">{s.target}</h2>
                    {ws && (
                      <span
                        className="mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px]"
                        style={{
                          borderColor: `color-mix(in oklab, ${ws.accent} 40%, transparent)`,
                          color: ws.accent,
                          background: `color-mix(in oklab, ${ws.accent} 12%, transparent)`,
                        }}
                      >
                        <Briefcase className="h-3 w-3" /> {ws.label}
                      </span>
                    )}
                    <div className="mt-1 font-mono text-xs text-muted-foreground">
                      {s.sourceCount ?? 0} collectors · confidence{" "}
                      {typeof s.confidence === "number" ? `${s.confidence}%` : "not calculated"}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 font-mono text-[10px] text-muted-foreground">
                    <span className={tone}>
                      <ShieldAlert className="mr-1 inline h-3 w-3" />TRUST {trust === null ? "—" : trust}
                    </span>
                    <span><FileDigit className="mr-1 inline h-3 w-3" />{s.sourceCount ?? 0}</span>
                    <span className={s.status === "completed" ? "text-success" : "text-amber"}>
                      {(s.status ?? "pending").toUpperCase()}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </ModuleShell>
    </AppLayout>
  );
}